import { useState } from "react";
import { toast } from "sonner";
import { Globe2, MapPin, ChevronDown, Loader2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ONLY_ALL_LABEL, SCOPE_ALL, effectiveScope, isCurrentOption, scopeLabel, scopeOptions } from "@/lib/scope";
import { ScopePicker, useActiveLocations } from "@/components/ScopePicker";

const CHIP = "font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 inline-flex items-center gap-1 border";

// Round J/L — the chip is always shown; only someone who may change it gets the picker.
// onChange receives { scope, user_location, scope_confirmed }.
export const ScopeChip = ({ item, canEdit, onChange, testId }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = useActiveLocations(open);
  const scope = effectiveScope(item);
  const Icon = scope === SCOPE_ALL ? Globe2 : MapPin;
  const tone = scope === SCOPE_ALL ? "text-tw-yellow border-tw-yellow/50 bg-tw-yellow/5" : "text-white border-white/30 bg-white/5";
  const label = scopeLabel(item);

  if (!canEdit) {
    return (
      <span className={`${CHIP} ${tone}`} data-testid={testId} data-scope={scope} data-editable="false" title="Waar deze memo voor geldt">
        <Icon size={9} strokeWidth={2.5} /> {label}
      </span>
    );
  }

  const pick = async (option) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/recordings/${item.id}/scope`, option.body);
      onChange?.({ scope: data.scope, user_location: data.user_location, scope_confirmed: true });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const trigger = (
    <button type="button" onClick={(e) => e.stopPropagation()} disabled={busy} aria-haspopup="listbox"
      data-testid={`${testId}-toggle`} data-scope={scope} title="Kies waar deze memo bij hoort"
      className={`tw-press group ${CHIP} ${tone} hover:border-tw-yellow transition-colors duration-150 disabled:opacity-60`}>
      <Icon size={9} strokeWidth={2.5} />
      <span data-testid={testId}>{label}</span>
      {busy ? <Loader2 size={9} className="animate-spin" /> : <ChevronDown size={9} strokeWidth={2.5} className="opacity-60 group-hover:opacity-100" />}
    </button>
  );

  return (
    <ScopePicker open={open} onOpenChange={setOpen} trigger={trigger} title="Waar hoort deze memo bij?"
      options={scopeOptions(user, active)} isCurrent={(o) => isCurrentOption(item, o)} onPick={pick} busy={busy}
      testId={`${testId}-picker`} />
  );
};

export const ScopeFilterToggle = ({ active, onToggle, testId }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={active}
    data-testid={testId}
    className={`tw-press h-11 px-3 inline-flex items-center justify-center gap-2 border font-mono text-[11px] uppercase tracking-[0.15em] whitespace-nowrap transition-colors duration-150 ${active ? "bg-tw-yellow text-black border-tw-yellow" : "bg-tw-surface text-tw-muted border-tw hover:text-white hover:border-tw-yellow"}`}
  >
    <Globe2 size={13} strokeWidth={2.5} /> {ONLY_ALL_LABEL}
  </button>
);

export const SavedScopeCard = ({ memo, onChange, onClose }) => (
  <div className="mb-6 flex flex-wrap items-center gap-3 bg-tw-surface border border-tw border-l-4 border-l-tw-yellow px-4 py-3 tw-fadeup" role="status" data-testid="saved-scope-card">
    <span className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase">Opgeslagen · geldt voor</span>
    <ScopeChip item={memo} canEdit onChange={onChange} testId="saved-scope-chip" />
    <span className="text-xs text-tw-muted flex-1 min-w-[10rem]">Klopt het niet? Tik op het label en kies.</span>
    <button type="button" onClick={onClose} aria-label="Sluiten" data-testid="saved-scope-close" className="p-1 text-tw-muted hover:text-white">
      <X size={14} strokeWidth={2.5} />
    </button>
  </div>
);
